/**
 * Editing the wording of an email the app sends on its own.
 *
 * Plain text, deliberately. Rich text would mean replacing the blanket
 * escaping that currently stops any HTML reaching a sponsor with an allowlist
 * sanitiser, plus an editor dependency, to gain bold and bullets. The wording
 * matters far more than the formatting, so the effort goes into making the
 * plain text easy to get right instead.
 *
 * THREE THINGS CARRY THAT:
 *
 *   Fields are highlighted where they sit, so it is obvious which parts of a
 *   sentence are filled in at send time and which are fixed.
 *
 *   The field menu offers only what resolves in THIS email, and says why for
 *   the ones it cannot. A reminder is built without a portal link, so offering
 *   one would let somebody mail a sponsor the literal text "{portal_link}".
 *
 *   The preview resolves against sample values, because a template full of
 *   braces is hard to read as a sentence.
 *
 * Nothing here decides what is available; that comes from the server, which is
 * also what sends the email. A second copy of those rules in the client would
 * drift from the one that matters.
 */

import { T } from '../lib/copyEdit.tsx';
import { useMemo, useRef, useState } from 'react';

export interface FieldInfo {
  label: string;
  sample: string;
}

/** One repeated line inside an email: a pattern applied per version, not prose
 *  somebody composes. Its fields are the fields of a single outcome. */
export interface LineMeta {
  key: string;
  name: string;
  hint: string;
  available: string[];
  required: string[];
}

export interface EmailTemplateMeta {
  key: string;
  name: string;
  subject: string;
  body: string;
  lines?: Record<string, string>;
  shippedSubject: string;
  shippedBody: string;
  shippedLines?: Record<string, string>;
  /** Empty for a template that assembles nothing, which is most of them. */
  lineMeta?: LineMeta[];
  available: string[];
  required: string[];
  unavailable: Record<string, string>;
}

export interface TemplateDraft {
  subject: string;
  body: string;
  /** Present only for a template with lines. Absent means "the shipped ones". */
  lines?: Record<string, string>;
}

const TOKEN = /\{[a-z_]+\}/g;

/** Splits text into plain runs and field tokens, so the highlight layer can
 *  mark the tokens without touching anything else. */
function segments(text: string): { text: string; token: boolean }[] {
  const out: { text: string; token: boolean }[] = [];
  let last = 0;
  for (const m of text.matchAll(TOKEN)) {
    if (m.index! > last) out.push({ text: text.slice(last, m.index), token: false });
    out.push({ text: m[0], token: true });
    last = m.index! + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), token: false });
  return out;
}

function tokensIn(text: string): string[] {
  return [...new Set(text.match(TOKEN) ?? [])];
}

/**
 * What is wrong with one piece of editable wording.
 *
 * Shared by the subject and body together and by each repeated line, because
 * the rules are the same and only the field list differs: a line resolves the
 * fields of a single version, a body the fields of the email. Written once so
 * the two cannot start disagreeing about what counts as a problem.
 */
function problemsFor(
  text: string,
  available: string[],
  required: string[],
  fields: Record<string, FieldInfo>,
  unavailable: Record<string, string>,
): string[] {
  const used = tokensIn(text);
  const out: string[] = [];

  for (const token of used) {
    const bare = token.slice(1, -1);
    if (!(bare in fields)) {
      out.push(`${token} is not a field, so it would be sent as written.`);
    } else if (!available.includes(bare)) {
      const why = unavailable[bare];
      out.push(
        `${token} does not resolve here${why ? ` (${why})` : ''}, so it would be sent as written.`,
      );
    }
  }

  for (const bare of required) {
    if (!used.includes(`{${bare}}`)) out.push(`{${bare}} is required here.`);
  }

  return out;
}

export default function EmailTemplateEditor({
  meta,
  fields,
  draft,
  onChange,
}: {
  meta: EmailTemplateMeta;
  fields: Record<string, FieldInfo>;
  draft: TemplateDraft;
  onChange: (next: TemplateDraft) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const subjectRef = useRef<HTMLTextAreaElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  /* The highlight layers scroll with their textareas. Without this the marks
     stay put while the words move under them, which the Invitation shows
     immediately: its default body is about fourteen visual lines against nine
     rows, so the very first template on the screen overflows. */
  const subjectHl = useRef<HTMLDivElement>(null);
  const bodyHl = useRef<HTMLDivElement>(null);

  function syncScroll(
    layer: React.RefObject<HTMLDivElement | null>,
    el: HTMLTextAreaElement,
  ) {
    if (!layer.current) return;
    layer.current.scrollTop = el.scrollTop;
    layer.current.scrollLeft = el.scrollLeft;
  }
  /** Which field the next insert lands in. Body unless the subject was the
   *  last thing touched. */
  const lastFocused = useRef<HTMLTextAreaElement | null>(null);

  const lineMeta = meta.lineMeta ?? [];
  const shippedLines = meta.shippedLines ?? {};
  /* The shipped lines until somebody edits one, so the inputs always open on
     the wording actually going out rather than on nothing. */
  const draftLines = draft.lines ?? shippedLines;

  const isDefault =
    draft.subject === meta.shippedSubject &&
    draft.body === meta.shippedBody &&
    lineMeta.every((l) => (draftLines[l.key] ?? '') === (shippedLines[l.key] ?? ''));

  const problems = useMemo(
    () =>
      problemsFor(
        `${draft.subject}\n${draft.body}`,
        meta.available,
        meta.required,
        fields,
        meta.unavailable,
      ),
    [draft.subject, draft.body, meta, fields],
  );

  function setLine(key: string, text: string) {
    onChange({ ...draft, lines: { ...draftLines, [key]: text } });
  }

  function insert(token: string) {
    const el = lastFocused.current ?? bodyRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next = el.value.slice(0, start) + token + el.value.slice(end);
    const caret = start + token.length;

    onChange(el === subjectRef.current ? { ...draft, subject: next } : { ...draft, body: next });

    // Put the caret after what was just inserted, once React has re-rendered.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  }

  /**
   * Resolves against samples so the preview reads as a finished sentence.
   *
   * An unresolvable field is left visible rather than blanked, so a mistake
   * shows up as a brace in the preview instead of a gap. `available` defaults
   * to the email's own fields and is passed explicitly for a line, which
   * resolves the fields of one version rather than of the email.
   */
  function resolve(text: string, available: string[] = meta.available): string {
    return text.replace(TOKEN, (token) => {
      const bare = token.slice(1, -1);
      const info = fields[bare];
      return info && available.includes(bare) ? info.sample : token;
    });
  }

  /* Split again AFTER resolving, which is what the send path does. {items} is
     a sample running to two paragraphs, because at send time it is a line per
     version; splitting only once would preview it as a single block and show a
     shape the sponsor never receives. */
  const previewParagraphs = draft.body
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .flatMap((p) =>
      resolve(p)
        .split(/\n{2,}/)
        .map((q) => q.trim())
        .filter(Boolean),
    );

  return (
    <div className="tpl">
      <div className="tpl-head">
        <h4 className="tpl-name">{meta.name}</h4>
        {!isDefault && <span className="tpl-edited"><T k="emailTpl.edited" d="Edited" /></span>}
        <button
          type="button"
          className="btn-compact btn-compact--quiet tpl-reset"
          disabled={isDefault}
          onClick={() =>
            onChange({
              subject: meta.shippedSubject,
              body: meta.shippedBody,
              ...(lineMeta.length > 0 ? { lines: { ...shippedLines } } : {}),
            })
          }
        >
          Reset to Default
        </button>
      </div>

      <label className="fl" htmlFor={`${meta.key}-subject`}><T k="emailTpl.field.subject" d="Subject" /></label>
      <Highlighted value={draft.subject} scrollRef={subjectHl}>
        <textarea
          id={`${meta.key}-subject`}
          ref={subjectRef}
          onScroll={(e) => syncScroll(subjectHl, e.currentTarget)}
          className="tpl-input tpl-input--subject"
          rows={1}
          value={draft.subject}
          spellCheck
          onFocus={() => { lastFocused.current = subjectRef.current; }}
          onChange={(e) => onChange({ ...draft, subject: e.target.value })}
        />
      </Highlighted>

      <label className="fl" htmlFor={`${meta.key}-body`}><T k="emailTpl.field.body" d="Body" /></label>
      <div className="tpl-bar">
        <div className="tpl-menu-wrap">
          <button
            type="button"
            className="btn-compact btn-compact--quiet"
            aria-haspopup="true"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            Insert Field
          </button>
          {menuOpen && (
            <div className="tpl-menu" role="menu">
              <p className="tpl-menu-label"><T k="emailTpl.fields.available" d="Available in this email" /></p>
              {meta.available.map((bare) => (
                <button
                  key={bare}
                  type="button"
                  role="menuitem"
                  className="tpl-mi"
                  onClick={() => { insert(`{${bare}}`); setMenuOpen(false); }}
                >
                  <code>{`{${bare}}`}</code>
                  <span>{fields[bare]?.label}</span>
                  {meta.required.includes(bare) && <em className="tpl-req">required</em>}
                </button>
              ))}

              {Object.keys(meta.unavailable).length > 0 && (
                <>
                  <p className="tpl-menu-label"><T k="emailTpl.fields.unavailable" d="Not available here" /></p>
                  {Object.entries(meta.unavailable).map(([bare, why]) => (
                    <div key={bare} className="tpl-mi tpl-mi--dead">
                      <code>{`{${bare}}`}</code>
                      <span>{why}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
        <span className="tpl-barnote"><T k="emailTpl.bodyHint" d="A blank line starts a new paragraph." /></span>
      </div>

      <Highlighted value={draft.body} scrollRef={bodyHl}>
        <textarea
          id={`${meta.key}-body`}
          ref={bodyRef}
          onScroll={(e) => syncScroll(bodyHl, e.currentTarget)}
          className="tpl-input"
          rows={9}
          value={draft.body}
          spellCheck
          onFocus={() => { lastFocused.current = bodyRef.current; }}
          onChange={(e) => onChange({ ...draft, body: e.target.value })}
        />
      </Highlighted>

      {problems.length > 0 ? (
        <div className="tpl-problems">
          {problems.map((p) => <p key={p}>{p}</p>)}
        </div>
      ) : (
        <p className="tpl-ok"><T k="emailTpl.ok" d="Every required field is present, and each one resolves here." /></p>
      )}

      {/* The repeated lines.
          Kept apart from the body because they are a different kind of writing:
          a pattern applied once per version, not a paragraph. Running them into
          the body as prose would suggest they can be reordered or merged, and
          they cannot — how many there are is decided at send time. */}
      {lineMeta.length > 0 && (
        <div className="tplines">
          <p className="fl" style={{ marginBottom: 2 }}>
            <T k="emailTpl.lines.title" d="The repeated lines" />
          </p>
          <p className="fdesc" style={{ marginTop: 0, marginBottom: 12 }}>
            <T
              k="emailTpl.lines.intro"
              d="One of these is written for each version, in place of {items} above. Which one gets used depends on the review, so all of them need to read well."
            />
          </p>

          {lineMeta.map((line) => {
            const text = draftLines[line.key] ?? '';
            const lineProblems = problemsFor(
              text,
              line.available,
              line.required,
              fields,
              meta.unavailable,
            );
            const changed = text !== (shippedLines[line.key] ?? '');

            return (
              <div className="tpline" key={line.key}>
                <div className="tpline-head">
                  <label className="tpline-name" htmlFor={`${meta.key}-line-${line.key}`}>
                    {line.name}
                  </label>
                  {changed && (
                    <span className="tpl-edited">
                      <T k="emailTpl.edited" d="Edited" />
                    </span>
                  )}
                </div>
                <p className="tpline-hint">{line.hint}</p>

                <Highlighted value={text}>
                  <textarea
                    id={`${meta.key}-line-${line.key}`}
                    className="tpl-input tpl-input--subject"
                    rows={1}
                    value={text}
                    spellCheck
                    onChange={(e) => setLine(line.key, e.target.value)}
                  />
                </Highlighted>

                {line.available.length > 0 && (
                  <p className="tpline-fields">
                    {line.available.map((bare) => (
                      <button
                        key={bare}
                        type="button"
                        className="tpline-chip"
                        title={fields[bare]?.label}
                        onClick={() => setLine(line.key, `${text}{${bare}}`)}
                      >
                        <code>{`{${bare}}`}</code>
                      </button>
                    ))}
                  </p>
                )}

                {lineProblems.length > 0 ? (
                  <div className="tpl-problems">
                    {lineProblems.map((p) => (
                      <p key={p}>{p}</p>
                    ))}
                  </div>
                ) : (
                  <p className="tpline-sample">{resolve(text, line.available)}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Same idea as Event Setup's "Where This Appears": a template full of
          braces is hard to read as a sentence, so it is shown finished. */}
      <p className="fl" style={{ marginTop: 14 }}><T k="eventSetup.told.whereTitle" d="Where This Appears" /></p>
      <div className="tpl-preview">
        <p className="tpl-preview-subject">{resolve(draft.subject)}</p>
        {previewParagraphs.map((p, i) => (
          <p key={i} className="tpl-preview-p">{p}</p>
        ))}
      </div>
      <p className="fdesc">
        Sample values, so you can read it as a sentence. A paragraph whose field has no value on a
        real send is left out rather than sent with a gap in it.
      </p>
    </div>
  );
}

/**
 * Draws the field highlights behind a textarea.
 *
 * A textarea cannot style its own contents, so an identically laid out layer
 * sits behind it holding the same text with the tokens marked. The two must
 * keep the same metrics or the boxes drift away from the words; that is
 * enforced in CSS by giving both the same font, padding and line height.
 */
function Highlighted({
  value,
  scrollRef,
  children,
}: {
  value: string;
  /* Only the two scrolling textareas need one. A line is a single row that
     never scrolls, so its highlight layer cannot drift out of step. */
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}) {
  return (
    <div className="tpl-ed">
      <div className="tpl-hl" ref={scrollRef} aria-hidden="true">
        {segments(value).map((s, i) =>
          s.token ? <mark key={i}>{s.text}</mark> : <span key={i}>{s.text}</span>,
        )}
        {/* Preserves a trailing newline, which a textarea shows and a div
            otherwise collapses. */}
        {'\n'}
      </div>
      {children}
    </div>
  );
}
